import { useId, type CSSProperties, type InputHTMLAttributes, type Ref } from "react";
import { KICKER_STYLE } from "./Kicker";
import { BUTTON_RESET, PRESS_CLASS } from "./press";
import "./Field.css";
import { scaledFont } from "@/styles/scaledFont";

export interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  /** The label's copy — always `t('…')`, never a literal. */
  label: string;
  /** A muted note at the end of the line, e.g. "UNKNOWN". */
  right?: string;
  /**
   * The end-of-line toggle — "unknown" on a birth-time field. It is the
   * same kicker `right` draws, on a button: one control that goes both
   * ways, always on the line, rather than an action that trades places
   * with a note once it has been used (01-ONBOARDING.md §3).
   */
  toggle?: {
    /** The kicker's copy — short, e.g. "UNKNOWN". Always `t('…')`. */
    label: string;
    /** The accessible name — the whole sentence. Always `t('…')`. */
    description: string;
    pressed: boolean;
    onToggle: () => void;
  };
  /**
   * The input's id. Defaults to a generated one; pass it only when
   * something outside the field has to point at the input by name — a
   * combobox's `aria-controls`, an external `aria-describedby`.
   */
  id?: string;
  /** Class and style go to the field, not to the input. */
  className?: string;
  style?: CSSProperties;
  /** Escape hatch for the input's own box. */
  inputStyle?: CSSProperties;
  /**
   * The input element itself, for a caller that has to measure or focus it
   * — `CityAutosuggest` positions its dropdown off the input's rect.
   */
  inputRef?: Ref<HTMLInputElement>;
}

const FIELD: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  borderBottom: "1px solid var(--d-line-2)",
  paddingBottom: "var(--space-3)",
};

/* Gap 12, from `Field` in
 * `templates/onboarding/onboarding-screen.jsx` — the field that carries an
 * end-of-line control, which is the one this component models. */
const LINE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 12,
};

/* No border, no box, no radius: the underline is the field. `--d-ramp-3` on
 * the caret is the one place the ramp is allowed outside a dot-work object —
 * it is two pixels wide and it blinks, which is exactly the "≤8% of the
 * frame" the rule is protecting. */
export const FIELD_INPUT_STYLE: CSSProperties = {
  flex: 1,
  background: "transparent",
  border: "none",
  outline: "none",
  padding: 0,
  font: scaledFont("500 16px/22px var(--font-body)"),
  color: "var(--d-fg)",
  caretColor: "var(--d-ramp-3)",
  minWidth: 0,
};

/* The end-of-line toggle: the muted kicker of `right`, on a real
 * `<button>`, lit to `--d-fg` while it is on. It never shrinks — the input
 * gives up the width. */
const TOGGLE: CSSProperties = {
  ...BUTTON_RESET,
  ...KICKER_STYLE,
  flex: "none",
  textAlign: "right",
};

/**
 * A single line of input — the whole form vocabulary of v2. Kicker label over
 * a bare input, ruled off with `--d-line-2`.
 *
 * Ported from `Field` in
 * `docs/design/handoff/design/templates/friends/friends-screen.jsx`. The
 * prototype's label is a loose `<span>`; here it is a real `<label htmlFor>`
 * over a generated id, so the field is reachable by its name.
 */
export function Field({
  label,
  right,
  toggle,
  id: givenId,
  className,
  style,
  inputStyle,
  inputRef,
  ...rest
}: FieldProps): JSX.Element {
  const generatedId = useId();
  const id = givenId ?? generatedId;
  return (
    <div
      className={className ? `d-v2-field ${className}` : "d-v2-field"}
      style={{ ...FIELD, ...style }}
    >
      <label htmlFor={id} style={KICKER_STYLE}>
        {label}
      </label>
      <span data-field-line style={LINE}>
        <input
          ref={inputRef}
          id={id}
          /* B15: a native date/time input's own chrome is reset in
           * `Field.css` — the reset is all pseudo-elements, which an inline
           * style cannot reach. */
          style={{ ...FIELD_INPUT_STYLE, ...inputStyle }}
          {...rest}
        />
        {right ? (
          <span style={{ ...KICKER_STYLE, color: "var(--d-fg-4)" }}>
            {right}
          </span>
        ) : null}
        {toggle ? (
          <button
            type="button"
            className={PRESS_CLASS}
            aria-pressed={toggle.pressed}
            aria-label={toggle.description}
            onClick={toggle.onToggle}
            style={{
              ...TOGGLE,
              color: toggle.pressed ? "var(--d-fg)" : "var(--d-fg-4)",
            }}
          >
            {toggle.label}
          </button>
        ) : null}
      </span>
    </div>
  );
}

export default Field;
